import { Chain, Timeframe } from '@ankr.com/chains-list';
import { useCallback } from 'react';

import { AccountPermission } from 'modules/permissions/constants';
import { AddNetworkButton } from 'modules/wallet/components/AddNetworkButton';
import { CopyEndpointModalWrapper } from 'modules/chains/components/CopyEndpointModal';
import { GuardAccountPermission } from 'modules/account/components/GuardAccountPermission';
import { chainsRoutesConfig } from 'domains/chains/routes';
import { hasPublicSubchains } from 'modules/chains/utils/hasPublicSubchains';
import { isMultichain } from 'modules/chains/utils/isMultichain';
import { selectPublicBlockchainCountByChainID } from 'modules/stats/store/public/selectors';
import { selectPublicStatsLoading } from 'modules/stats/actions/fetchPublicStats';
import { useAppSelector } from 'store/useAppSelector';
import { useChainsPublicStatsParams } from 'modules/chains/components/ChainsPublicStatsProvider';
import { useSignInWithRedirectURL } from 'modules/auth/hooks/useSignInWithRedirectURL';

import { BaseChainsCard, IBaseChainCardProps } from '../../../BaseChainsCard';
import { EChainView } from '../../../ChainViewSelector';
import { usePublicChainsItemStyles } from './usePublicChainsItemStyles';

export interface IChainCardProps {
  chain: Chain;
  onOpenUpgradeModal: () => void;
  timeframe: Timeframe;
  view?: EChainView;
}

interface PublicChainCardProps extends IChainCardProps {}

const EMPTY_JWT_TOKENS = [
  {
    id: '',
    index: 0,
    userEndpointToken: '',
    jwtData: '',
    name: '',
    description: '',
  },
];

export const PublicChainCard = ({
  chain,
  onOpenUpgradeModal,
  timeframe,
  view = EChainView.Cards,
}: PublicChainCardProps) => {
  const signIn = useSignInWithRedirectURL({
    hasAPIKeysNotification: true,
    redirectURL: chainsRoutesConfig.chainDetails.generatePath(chain.id),
  });

  const publicStatsParams = useChainsPublicStatsParams();

  const totalRequests = useAppSelector(state =>
    selectPublicBlockchainCountByChainID(state, publicStatsParams, chain.id),
  );

  const loading = useAppSelector(state =>
    selectPublicStatsLoading(state, publicStatsParams),
  );

  const totalRequestsStr = totalRequests.toString();

  const { classes, cx } = usePublicChainsItemStyles();

  const hasPublicEndpoints = hasPublicSubchains(chain);
  const isEndpointLocked = !hasPublicEndpoints || isMultichain(chain.id);

  const onLockedChainClick = useCallback(() => {
    if (isMultichain(chain.id) || (!chain.isPublic && !chain.premiumOnly)) {
      signIn();
    } else {
      onOpenUpgradeModal();
    }
  }, [chain.id, chain.isPublic, chain.premiumOnly, onOpenUpgradeModal, signIn]);

  const isListView = view === EChainView.List;

  const actions = (
    <GuardAccountPermission permission={AccountPermission.ChainItem}>
      <div
        className={cx(classes.publicChainActions, {
          [classes.publicChainActionsListView]: isListView,
        })}
      >
        <CopyEndpointModalWrapper
          jwtTokens={EMPTY_JWT_TOKENS}
          chain={chain}
          userEndpointToken=""
          buttonProps={{
            variant: 'outlined',
            fullWidth: true,
            onClick: isEndpointLocked ? onLockedChainClick : undefined,
          }}
          hasConsensusSwitch={false}
          buttonClassName={cx(classes.publicChainCopyEndpointButton, {
            [classes.publicChainCopyEndpointButtonListView]: isListView,
          })}
        />
        <AddNetworkButton
          chain={chain}
          hasAddNetworkDialog
          hasPlaceholder={isListView}
          onClick={isEndpointLocked ? onLockedChainClick : undefined}
          size="small"
        />
      </div>
    </GuardAccountPermission>
  );

  const cardProps: IBaseChainCardProps = {
    isPublicLayout: true,
    view,
    chain,
    loading,
    timeframe,
    totalRequests: totalRequestsStr,
    actions,
  };

  return <BaseChainsCard {...cardProps} />;
};
